<template>
  <Popover :placement="placement" :distance="distance">
    <template #trigger="triggerCtx">
      <slot name="trigger" v-bind="triggerCtx" />
    </template>
    <template #dropdown="{ closeDropdown }">
      <ul role="menu" class="tw:m-0! tw:list-none tw:overflow-hidden tw:rounded-md tw:py-1 tw:p-0!">
        <template v-for="(item, i) in items" :key="item.key ?? i">
          <li role="none" :class="item.separator ? '' : 'tw:contents'">
            <RouterLink
              v-if="item.to"
              :to="item.to"
              role="menuitem"
              :class="cn(ITEM_BASE, itemRadius(i), item.class)"
              @click="onItemClick(item, closeDropdown, $event)">
              <component v-if="item.icon" :is="item.icon" :size="16" :stroke-width="2" class="tw:shrink-0" />
              <span v-else-if="reserveIconSpace" aria-hidden="true" class="tw:inline-block tw:size-4 tw:shrink-0" />
              <span class="tw:min-w-0 tw:flex-1 tw:truncate">{{ item.label }}</span>
            </RouterLink>
            <a
              v-else-if="item.href"
              :href="item.href"
              :target="item.target"
              :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
              role="menuitem"
              :class="cn(ITEM_BASE, itemRadius(i), item.class)"
              @click="onItemClick(item, closeDropdown, $event)">
              <component v-if="item.icon" :is="item.icon" :size="16" :stroke-width="2" class="tw:shrink-0" />
              <span v-else-if="reserveIconSpace" aria-hidden="true" class="tw:inline-block tw:size-4 tw:shrink-0" />
              <span class="tw:min-w-0 tw:flex-1 tw:truncate">{{ item.label }}</span>
            </a>
            <button
              v-else
              type="button"
              role="menuitem"
              :disabled="item.disabled"
              :class="cn(ITEM_BASE, itemRadius(i), item.disabled && DISABLED, item.class)"
              @click="onItemClick(item, closeDropdown, $event)">
              <component v-if="item.icon" :is="item.icon" :size="16" :stroke-width="2" class="tw:shrink-0" />
              <span v-else-if="reserveIconSpace" aria-hidden="true" class="tw:inline-block tw:size-4 tw:shrink-0" />
              <span class="tw:min-w-0 tw:flex-1 tw:truncate">{{ item.label }}</span>
            </button>
          </li>
          <li v-if="item.separator" role="separator" class="tw:my-1 tw:h-px tw:bg-border-secondary" />
        </template>
      </ul>
    </template>
  </Popover>
</template>

<script setup lang="ts">
// LeadPopoverMenu — thin wrapper around Popover for dropdown-style menus.
// The items[] config keeps all menu rows consistent (text-13, gap-2, hover:bg-gray-50) so no caller
// needs to hand-write the styling. Per-item `class` lets callers customise if needed
// escaping the abstraction; the icon slot / label / separator are all data-driven.
import { computed, type Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { RouterLink } from 'vue-router';
import Popover from '@/components/common/Popover.vue';
import { cn } from '@/lib/cn';

export interface LeadPopoverMenuItem {
  /** Displayed label. */
  label: string;
  /** Stable key for :key iteration. Falls back to index. */
  key?: string | number;
  /** Leading icon (Tabler component). Omit for a label-only row. */
  icon?: Component;
  /** Renders as a RouterLink when set. */
  to?: RouteLocationRaw;
  /** Renders as an anchor when set (and `to` is not). */
  href?: string;
  target?: '_self' | '_blank' | '_parent' | '_top';
  /** Fired on click for button-style items. RouterLink/anchor items still emit if provided. */
  onClick?: (event: MouseEvent) => void;
  /** Extra classes appended AFTER base styles — use for `tw:text-red-600!` etc. */
  class?: string;
  disabled?: boolean;
  /** Render an <hr> separator directly below this item. */
  separator?: boolean;
}

const props = withDefaults(
  defineProps<{
    items: LeadPopoverMenuItem[];
    placement?: string;
    distance?: number;
  }>(),
  {
    placement: 'bottom-end',
    distance: 6,
  }
);

// Base row styling — the whole point of this component. Callers can override per-item via `class`
// (tailwind-merge in cn() means a later class wins) but they never write these baselines.
const ITEM_BASE = [
  'tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-3 tw:py-2',
  'tw:text-left tw:text-13! tw:text-gray-900!',
  'tw:cursor-pointer tw:border-0 tw:bg-transparent tw:no-underline',
  'tw:hover:bg-gray-50 tw:hover:text-gray-900',
  'tw:focus-visible:bg-gray-50 tw:focus-visible:outline-none',
].join(' ');

const DISABLED = 'tw:pointer-events-none tw:opacity-50';

// If ANY item in the menu carries an icon, reserve a 16px slot on every other row so the labels
// line up in a single column. If no item has an icon, we don't reserve — labels sit flush at the
// row's left padding and the mixed-icon jagged left edge doesn't arise.
const reserveIconSpace = computed(() => props.items.some((item) => !!item.icon));

/**
 * Round the top of the first item and the bottom of the last item to match the panel's `rounded-md`.
 * Without this, the hover fill's square corners spill past the panel's rounded ones at top/bottom.
 * Interior rows stay square. Separators are items too — a separator directly under the first row
 * still lets that row keep its top rounding; a separator as the last item is unusual but treated
 * as a plain divider (nothing to round below it).
 */
function itemRadius(index: number): string {
  const isFirst = index === 0;
  const isLast = index === props.items.length - 1 && !props.items[index]?.separator;
  return cn(isFirst && 'tw:rounded-t-md', isLast && 'tw:rounded-b-md');
}

function onItemClick(item: LeadPopoverMenuItem, closeDropdown: () => void, event?: MouseEvent) {
  if (item.disabled) return;
  item.onClick?.(event as MouseEvent);
  closeDropdown();
}
</script>
